import { getBackendUrl } from '@/lib/backendUrl';
import { getApiError } from '@/lib/utils';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Users, CheckCircle2, CircleDashed, FileCheck2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import InvoiceDraftDialog from './InvoiceDraftDialog';

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const BLOCKING_LABELS = {
  missing_or_ambiguous_target: 'has no resolved QBO mapping yet.',
  no_site_on_job: 'has no site on this job.',
  no_billable_lines: 'has no billable service lines.',
  no_auditors_assigned: 'has no auditors assigned.',
  auditor_region_unassigned: 'has no QBO Region assigned yet — set it in Sales Invoicing > Settings.',
};

function unresolvedText(u) {
  const suffix = BLOCKING_LABELS[u.reason] || u.reason.replace(/_/g, ' ');
  return u.label ? `${u.label} ${suffix}` : suffix;
}

function formatDateOnly(isoDateStr) {
  if (!isoDateStr) return null;
  const [y, m, d] = isoDateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB');
}

function MappingBadge({ mapping }) {
  if (!mapping || mapping.status !== 'resolved') {
    return <Badge variant="secondary" className="bg-amber-100 text-amber-800"><CircleDashed className="h-3 w-3 mr-1" />Decision needed</Badge>;
  }
  if (mapping.needs_check_in_qbo) {
    return (
      <Badge variant="secondary" className="bg-sky-100 text-sky-800" title="Mapped locally but not yet confirmed active in QBO.">
        {mapping.resolved_reference_name} — Check in QBO
      </Badge>
    );
  }
  return <Badge className="bg-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" />{mapping.resolved_reference_name}</Badge>;
}

export default function JobPreviewDialog({ jobId, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showDraft, setShowDraft] = useState(false);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    axios.get(`${API}/api/sales-invoicing/jobs/${jobId}/preview`, { headers: authHeaders() })
      .then(({ data }) => setPreview(data))
      .catch((err) => {
        setLoadError(true);
        toast.error(getApiError(err, 'Could not load this job preview.'));
      })
      .finally(() => setLoading(false));
  }, [jobId]);

  const redownloadBatch = async (batchId) => {
    try {
      const res = await axios.get(`${API}/api/sales-invoicing/export/batches/${batchId}/download`, {
        headers: authHeaders(), responseType: 'blob',
      });
      const match = /filename="?([^"]+)"?/.exec(res.headers['content-disposition'] || '');
      const filename = match ? match[1] : 'sales_invoicing_staging.xlsx';
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // responseType 'blob' means a JSON error body also arrives as a
      // Blob — parse it back to text so the real detail message shows.
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          err.response.data = JSON.parse(text);
        } catch { /* leave as-is if it isn't JSON */ }
      }
      toast.error(getApiError(err, 'Could not redownload this export.'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="job-preview-dialog">
        <DialogHeader>
          <DialogTitle>{preview?.hotel_name || 'Job preview'}</DialogTitle>
        </DialogHeader>

        {loading && <div className="text-sm text-slate-500" data-testid="job-preview-loading">Loading…</div>}
        {!loading && loadError && <div className="text-sm text-red-600" data-testid="job-preview-error">Could not load this job preview — try again.</div>}

        {!loading && !loadError && preview && (
          <div className="space-y-5" data-testid="job-preview-content">
            <div className="flex flex-wrap gap-2 text-sm text-slate-500">
              <span>{formatDateOnly(preview.scheduled_date)}</span>
              <span>&middot;</span>
              <span>{preview.job_reference}</span>
              {preview.already_exported && (
                <Badge className="bg-slate-700" data-testid="job-preview-exported-badge">
                  <FileCheck2 className="h-3 w-3 mr-1" />Already exported — Invoice #{preview.already_exported_detail?.invoice_number ?? '?'}
                </Badge>
              )}
              {preview.already_exported && preview.already_exported_detail?.has_workbook && (
                <Button
                  size="sm" variant="outline" className="h-6 px-2 text-xs"
                  data-testid="job-preview-redownload-btn"
                  onClick={() => redownloadBatch(preview.already_exported_detail.batch_id)}
                >
                  Redownload
                </Button>
              )}
              {preview.already_exported && !preview.already_exported_detail?.has_workbook && (
                <span className="text-xs text-slate-400" data-testid="job-preview-no-workbook-note">(exported before redownload was available)</span>
              )}
              {preview.possible_qbo_match && (
                <Badge className="bg-orange-500" data-testid="job-preview-possible-match-badge">
                  Possible QBO match — Invoice #{preview.possible_qbo_match_detail?.invoice_number ?? '?'}
                </Badge>
              )}
            </div>

            <div>
              <div className="text-xs font-medium text-slate-400 uppercase mb-1">Site → Customer</div>
              <MappingBadge mapping={preview.site_mapping} />
            </div>

            <div>
              <div className="text-xs font-medium text-slate-400 uppercase mb-1 flex items-center gap-1"><Users className="h-3 w-3" />Auditor roster → Class</div>
              <div className="text-sm text-slate-700 mb-1">{preview.auditor_names.join(', ') || '— none assigned —'}</div>
              <MappingBadge mapping={preview.auditor_mapping} />
            </div>

            <div>
              <div className="text-xs font-medium text-slate-400 uppercase mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" />Region</div>
              {preview.region ? (
                <Badge className="bg-emerald-600">{preview.region}</Badge>
              ) : (
                <Badge variant="outline" className="border-amber-400 text-amber-700" data-testid="job-preview-region-unresolved">Region unresolved</Badge>
              )}
            </div>

            <div>
              <div className="text-xs font-medium text-slate-400 uppercase mb-2">Service components → Item</div>
              <div className="space-y-2" data-testid="job-preview-service-lines">
                {preview.service_mappings.length === 0 && <div className="text-sm text-slate-500">No billable lines on this job.</div>}
                {preview.service_mappings.map((s) => (
                  <div key={s.pricing_line_id} className="flex items-center justify-between gap-3 text-sm border rounded-md p-2" data-testid={`job-preview-service-line-${s.pricing_line_id}`}>
                    <div>
                      <div className="font-medium">{s.service_name}</div>
                      <div className="text-slate-400 text-xs">£{s.price.toFixed(2)}</div>
                    </div>
                    <MappingBadge mapping={s.mapping} />
                  </div>
                ))}
              </div>
            </div>

            {preview.unresolved_targets.length > 0 && (
              <div data-testid="job-preview-unresolved-targets">
                <div className="text-xs font-medium text-amber-700 uppercase mb-2">Unresolved QBO targets</div>
                <ul className="space-y-1">
                  {preview.unresolved_targets.map((u, idx) => (
                    <li key={idx} className="text-sm text-amber-800 flex items-center gap-2" data-testid={`job-preview-unresolved-${idx}`}>
                      <CircleDashed className="h-3 w-3 flex-shrink-0" />
                      {u.reason ? unresolvedText(u) : (u.label || '')}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="pt-2 border-t">
              <Button
                onClick={() => setShowDraft(true)}
                data-testid="job-preview-view-draft-btn"
                className="w-full"
              >
                <Receipt className="h-4 w-4 mr-2" />
                Review invoice draft
              </Button>
            </div>
          </div>
        )}
      </DialogContent>

      {showDraft && (
        <InvoiceDraftDialog jobId={jobId} onClose={() => setShowDraft(false)} />
      )}
    </Dialog>
  );
}
